import React, { useState, useEffect, useMemo } from 'react';
import { subscribeToUserCollection, addUserDoc, updateUserDoc, deleteUserDoc } from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/inventory-pages.css';
import useCurrencyTools from '../hooks/useCurrencyTools';

const PACKAGING_TYPES = [
  'Box',
  'Wrap Band',
  'Shrink Wrap',
  'Paper Bag',
  'Tin / Container',
  'Label',
  'Ribbon / Twine',
  'Insert Card',
  'Other',
];

const emptyForm = {
  name: '',
  type: '',
  dimensions: '',
  costPerUnit: '',
  stock: '',
  unit: 'pcs',
  supplier: '',
  notes: '',
};

export default function PackagingPage() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const { preferredCurrency: currency, currencySymbol, formatMoney, convertAndFormat, convertMoney } = useCurrencyTools();

  useEffect(() => {
    const unsubs = [
      subscribeToUserCollection('packaging', setItems),
      subscribeToUserCollection('suppliers', setSuppliers),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      name: item.name || '',
      type: item.type || '',
      dimensions: item.dimensions || '',
      costPerUnit: item.costPerUnit ?? '',
      stock: item.stock ?? '',
      unit: item.unit || 'pcs',
      supplier: item.supplier || '',
      notes: item.notes || '',
    });
    setEditId(item.id);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setSaving(true);
    try {
      const data = {
        ...form,
        costPerUnit: form.costPerUnit !== '' ? Number(form.costPerUnit) : null,
        stock: form.stock !== '' ? Number(form.stock) : null,
        currencyCode: currency,
      };
      if (editId) {
        await updateUserDoc('packaging', editId, data);
      } else {
        await addUserDoc('packaging', data);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditId(null);
    } catch (err) {
      console.error('Error saving packaging:', err);
      alert('Failed to save packaging item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this packaging item?')) return;
    try {
      await deleteUserDoc('packaging', id);
    } catch (err) {
      console.error('Error deleting packaging:', err);
    }
  };

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.name || '').toLowerCase().includes(q) ||
      (i.type || '').toLowerCase().includes(q) ||
      (i.supplier || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const totalValue = items.reduce((sum, i) => {
    const base = (i.costPerUnit || 0) * (i.stock || 0);
    return sum + convertMoney(base, i.currencyCode || currency, currency);
  }, 0);
  const lowStock = items.filter(i => (i.stock || 0) > 0 && (i.stock || 0) < 20).length;
  const supplierNames = [...new Set(suppliers.map(s => s.name).filter(Boolean))];

  const getStockBadge = (stock) => {
    if (stock == null) return null;
    if (stock === 0) return <span className="inv-badge inv-badge-red">Out of stock</span>;
    if (stock < 20) return <span className="inv-badge inv-badge-amber">Low: {stock}</span>;
    return <span className="inv-badge inv-badge-green">{stock} in stock</span>;
  };

  return (
    <>
      <div className="sl-page inventory-subpage">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Inventory', to: '/inventory' },
            { label: 'Packaging' },
          ]} />
          <div className="pageheader-row">
            <div className="pageheading">Packaging</div>
            <div className="pagedescription">Track packaging materials, stock, and costs.</div>
          </div>
        </div>

        <div className="inventory-subpage-content borderleft">
          {/* Stats */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-stats-row">
              <div className="inv-stat-item">
                <span className="inv-stat-label">Items</span>
                <span className="inv-stat-value">{items.length}</span>
              </div>
              <div className="inv-stat-item">
                <span className="inv-stat-label">Stock Value</span>
                <span className="inv-stat-value">{formatMoney(totalValue, currency, { maximumFractionDigits: 0 })}</span>
              </div>
              {lowStock > 0 && (
                <div className="inv-stat-item">
                  <span className="inv-stat-label">Low Stock</span>
                  <span className="inv-stat-value" style={{ color: '#d97706' }}>{lowStock}</span>
                </div>
              )}
            </div>
          </section>

          {/* Toolbar */}
          <section className="inv-card mypadding borderbottom">
            <div className="inv-search">
              <input
                type="text"
                placeholder="Search packaging..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button className="inv-btn inv-btn-primary" onClick={openCreate}>
                + Add Packaging
              </button>
            </div>
          </section>

          {/* Cards */}
          <section className="inv-card mypaddingmoretop">
            {filtered.length === 0 ? (
              <div className="inv-empty">
                <p>{items.length === 0 ? 'No packaging items added yet.' : 'No items match your search.'}</p>
                {items.length === 0 && (
                  <button className="inv-btn inv-btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>
                    Add First Item
                  </button>
                )}
              </div>
            ) : (
              <div className="inv-card-grid">
                {filtered.map(item => (
                  <div key={item.id} className="inv-card-item">
                    <div className="inv-card-item-header">
                      <div>
                        <div className="inv-card-item-title">{item.name}</div>
                        {item.type && <span className="inv-badge inv-badge-gray" style={{ marginTop: 4 }}>{item.type}</span>}
                      </div>
                      {getStockBadge(item.stock)}
                    </div>
                    <div className="inv-card-item-meta">
                      {item.dimensions && <span>{item.dimensions}</span>}
                      {item.costPerUnit != null && (
                        <span>
                          {convertAndFormat(item.costPerUnit, item.currencyCode || currency, currency, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/{item.unit}
                          {(item.currencyCode && item.currencyCode !== currency) && (
                            <span style={{ color: '#6b7280', marginLeft: 6 }}>
                              (Orig {formatMoney(item.costPerUnit, item.currencyCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                            </span>
                          )}
                        </span>
                      )}
                      {item.supplier && <span>{item.supplier}</span>}
                      {item.notes && <span style={{ color: 'var(--sl-gray-400)', fontStyle: 'italic' }}>{item.notes}</span>}
                    </div>
                    <div className="inv-card-item-actions">
                      <button className="inv-btn inv-btn-secondary inv-btn-sm" onClick={() => openEdit(item)}>Edit</button>
                      <button className="inv-btn inv-btn-danger inv-btn-sm" onClick={() => handleDelete(item.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      

      {/* Modal */}
      {showModal && (
        <div className="inv-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <h2>{editId ? 'Edit Packaging' : 'Add Packaging'}</h2>
            <form onSubmit={handleSave}>
              <div className="inv-form-grid">
                <div className="inv-field">
                  <label>Name *</label>
                  <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Kraft Paper Box" required />
                </div>
                <div className="inv-field">
                  <label>Type</label>
                  <select value={form.type} onChange={e => set('type', e.target.value)}>
                    <option value="">Select type</option>
                    {PACKAGING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="inv-field">
                  <label>Dimensions</label>
                  <input value={form.dimensions} onChange={e => set('dimensions', e.target.value)} placeholder="e.g. 8×6×3 cm" />
                </div>
                <div className="inv-field">
                  <label>Cost per Unit ({currencySymbol})</label>
                  <input type="number" step="any" min="0" value={form.costPerUnit} onChange={e => set('costPerUnit', e.target.value)} placeholder="0.00" />
                </div>
                <div className="inv-field">
                  <label>Stock</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} placeholder="0" style={{ flex: 1 }} />
                    <select value={form.unit} onChange={e => set('unit', e.target.value)} style={{ width: 80 }}>
                      <option value="pcs">pcs</option>
                      <option value="rolls">rolls</option>
                      <option value="sheets">sheets</option>
                      <option value="m">m</option>
                    </select>
                  </div>
                </div>
                <div className="inv-field">
                  <label>Supplier</label>
                  <input
                    list="pkg-supplier-list"
                    value={form.supplier}
                    onChange={e => set('supplier', e.target.value)}
                    placeholder="Select or type"
                  />
                  <datalist id="pkg-supplier-list">
                    {supplierNames.map(n => <option key={n} value={n} />)}
                  </datalist>
                </div>
                <div className="inv-field" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes..." />
                </div>
              </div>
              <div className="inv-modal-actions">
                <button type="button" className="inv-btn inv-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn inv-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Update' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
      </div>
    </>
  );
}
